'use client';

import { useEffect, useState } from 'react';
import { Link } from '@/navigation';
import { useTranslations } from 'next-intl';
import { CustomError } from '@nf/utils-common/errors';
import * as Sentry from '@sentry/nextjs';

// 這些錯誤碼只顯示後端訊息中的 Message，不顯示 LogId
const MESSAGE_ONLY_ERROR_CODES = ['SPA-1017', 'SP-1068'];

export default function Error({
	error,
	reset,
}: {
	error: Error & { digest?: string },
	reset: () => void
}) {
	const t = useTranslations('General');
	const year = new Date().getFullYear();
	const [isChunkLoadError, setIsChunkLoadError] = useState(false);
	const customErrorData = error instanceof CustomError
		? error.errorData
		: undefined;
	const displayErrorMessage = MESSAGE_ONLY_ERROR_CODES.includes(customErrorData?.code ?? '')
		? error.message.match(/(?:^|,\s*)Message\s*:\s*(.*)$/is)?.[1].trim() ?? error.message
		: error.message;

	useEffect(() => {
		console.error(error);

		// ChunkLoadError 是常見的網路/快取問題，不送 Sentry 避免雜訊
		if (error.name !== 'ChunkLoadError') {
			Sentry.captureException(error, {
				tags: {
					error_context: 'application_error',
					error_name: error.name,
				},
				extra: {
					errorMessage: error.message,
					errorDigest: error.digest ?? '',
					errorStack: error.stack,
					errorCode: customErrorData?.code,
					errorDataDigest: customErrorData?.digest,
					pathname: window.location.pathname,
				},
			});
		}

		if (error.name === 'ChunkLoadError') { // ex. name = ChunkLoadError, message = Loading chunk 7921 failed.
			setIsChunkLoadError(true);
		}
	}, [error]);

	return (
		<>
			<div className="system">
				<header className="system__header"></header>
				<div className="system__main">
					<div className="error">
						<div className="error__img">
							<div className="error__img-text"></div>
							<div className="error__img-player">
								<span></span>
							</div>
							<div className="error__img-hit"></div>
							<div className="error__img-ball"></div>
						</div>
						{
							isChunkLoadError ? (
								<ConnectionInterruptMsg />
							) : (
								<div className="error__info">
									<div className="error__label">
										<span className="error__text">{customErrorData?.code}</span>
									</div>
									<div className="error__code">{customErrorData?.digest ?? error?.digest}</div>
									<div className="error__title">OOPS!</div>
									<div className="error__msg">
										<div className="error__subtitle">Something is wrong.</div>
										<div className="error__desc">{displayErrorMessage}</div>
										<Link
											className="system-btn"
											href="/"
										>
											{t('lbl_Back')}
										</Link>
									</div>
								</div>
							)
						}
					</div>
				</div>
				<footer className="system__footer">
					<span className="copyright">© Copyright {year}</span>
				</footer>
			</div>
		</>
	);
}

const ConnectionInterruptMsg = () => {
	const t = useTranslations('General');

	return (
		<div className="error__info">
			<div className="error__title">OOPS!</div>
			<div className="error__msg">
				<div className="error__subtitle">{t('lbl_MarsConnection1')}</div>
				<div className="error__desc">{t('lbl_MarsConnection2')}</div>
			</div>
		</div>
	)
}
